import { useParams, Link } from 'react-router-dom'
import { Badge } from '@/shared/components/atoms/ui/badge'
import { Button } from '@/shared/components/atoms/ui/button'
import { Card, CardContent } from '@/shared/components/atoms/ui/card'
import { useProvider } from '../hooks/useProviders'
import { ProviderLifecycleStatus, RiskLevel } from '@kfolks/types'
import { Icon } from '@/shared/components/atoms/Icon'

const LIFECYCLE_BADGE_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
	[ProviderLifecycleStatus.ACTIVE]: 'default',
	[ProviderLifecycleStatus.PLANNED]: 'outline',
	[ProviderLifecycleStatus.POC]: 'secondary',
	[ProviderLifecycleStatus.TERMINATED]: 'destructive',
}

const RISK_COLORS: Record<string, string> = {
	[RiskLevel.HIGH]: 'bg-red-100 text-red-800',
	[RiskLevel.MEDIUM]: 'bg-yellow-100 text-yellow-800',
	[RiskLevel.LOW]: 'bg-green-100 text-green-800',
}

const DATA_CLASSIFICATION_LABELS: Record<string, string> = {
	pii: 'PII',
	financial_data: 'Financial',
	health_data: 'Health',
	intellectual_property: 'IP',
	public: 'Public',
	internal: 'Internal',
	confidential: 'Confidential',
	restricted: 'Restricted',
}

export function ProviderDetailPage() {
	const { id } = useParams<{ id: string }>()
	const { data: provider, isLoading, error } = useProvider(id ?? '')

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-24">
				<Icon name="LoaderCircle" size={32} className="animate-spin text-muted-foreground" />
			</div>
		)
	}

	if (error || !provider) {
		return (
			<div className="space-y-4">
				<Link to="/providers">
					<Button variant="ghost" size="sm">
						<Icon name="ArrowLeft" size="sm" className="mr-1" /> Back to Providers
					</Button>
				</Link>
				<div className="rounded-lg bg-red-50 p-6 text-center text-red-700">Provider not found</div>
			</div>
		)
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-4">
				<Link to="/providers">
					<Button variant="ghost" size="sm">
						<Icon name="ArrowLeft" size="sm" className="mr-1" /> Providers
					</Button>
				</Link>
				<div>
					<h1 className="text-2xl font-bold text-foreground">{provider.name}</h1>
					<p className="text-sm font-mono text-muted-foreground">{provider.code}</p>
				</div>
				<div className="ml-auto flex gap-2">
					<Badge variant={LIFECYCLE_BADGE_VARIANT[provider.lifecycleStatus] ?? 'secondary'}>
						{provider.lifecycleStatus}
					</Badge>
				</div>
			</div>

			{provider.description && <p className="text-sm text-muted-foreground">{provider.description}</p>}

			<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
				<Card>
					<CardContent className="p-4">
						<h3 className="text-xs font-medium text-muted-foreground mb-1">Risk Level</h3>
						<span
							className={`inline-flex items-center px-2 py-1 rounded text-sm font-medium ${RISK_COLORS[provider.riskLevel] ?? 'bg-muted'}`}
						>
							{provider.riskLevel}
						</span>
					</CardContent>
				</Card>

				<Card>
					<CardContent className="p-4">
						<h3 className="text-xs font-medium text-muted-foreground mb-1">Business Impact</h3>
						<span className="text-sm font-medium text-foreground">
							{provider.businessImpact === 'critical' ? 'Critical' : 'Non-critical'}
						</span>
					</CardContent>
				</Card>

				<Card>
					<CardContent className="p-4">
						<h3 className="text-xs font-medium text-muted-foreground mb-1">Owner</h3>
						<span className="text-sm text-foreground">
							{provider.ownerId ? provider.ownerId.substring(0, 8) + '...' : 'Not assigned'}
						</span>
					</CardContent>
				</Card>
			</div>

			{provider.dataClassification && provider.dataClassification.length > 0 && (
				<Card>
					<CardContent className="p-4">
						<h3 className="text-xs font-medium text-muted-foreground mb-2">Data Classification</h3>
						<div className="flex flex-wrap gap-2">
							{provider.dataClassification.map((dc) => (
								<span key={dc} className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-50 text-blue-700">
									{DATA_CLASSIFICATION_LABELS[dc] ?? dc}
								</span>
							))}
						</div>
					</CardContent>
				</Card>
			)}

			<Card>
				<CardContent className="p-4">
					<h3 className="text-xs font-medium text-muted-foreground mb-1">Status</h3>
					<span className={`text-sm font-medium ${provider.isActive ? 'text-green-700' : 'text-red-700'}`}>
						{provider.isActive ? 'Active' : 'Inactive'}
					</span>
				</CardContent>
			</Card>
		</div>
	)
}
